import { AllowedDateFormats, AllowedDatetimeFormats, ITableColumn } from 'types'
import { parseTemporalByFormat } from 'components/spreadsheet/helpers/paste'

export const DATE_FORMAT_CANDIDATES: AllowedDateFormats[] = [
  'DD/MM/YYYY',
  'DD-MM-YYYY',
  'MM/DD/YYYY',
  'MM-DD-YYYY',
  'YYYY/MM/DD',
  'YYYY-MM-DD'
]

export const detectLikelyDateFormat = (dateStrings: string[]): AllowedDateFormats => {
  const formatScores = new Map<AllowedDateFormats, number>()
  DATE_FORMAT_CANDIDATES.forEach((fmt) => formatScores.set(fmt, 0))

  for (const raw of dateStrings) {
    const value = String(raw ?? '').trim()
    if (!value) continue

    for (const fmt of DATE_FORMAT_CANDIDATES) {
      if (parseTemporalByFormat(value, fmt, { kind: 'date' })) {
        formatScores.set(fmt, (formatScores.get(fmt) || 0) + 1)
        break // Use the first format that parses successfully
      }
    }
    // If no format matched, this string does not contribute to any score.
  }

  // Pick the candidate with the highest score
  let bestFormat: AllowedDateFormats = DATE_FORMAT_CANDIDATES[0]
  let bestScore = -1
  for (const [fmt, score] of formatScores.entries()) {
    if (score > bestScore) {
      bestFormat = fmt
      bestScore = score
    }
  }
  return bestFormat
}

export const DATETIME_FORMAT_CANDIDATES: AllowedDatetimeFormats[] = [
  'DD/MM/YYYY HH:mm',
  'DD/MM/YYYY HH:mm:ss',
  'MM/DD/YYYY HH:mm',
  'MM/DD/YYYY HH:mm:ss',
  'YYYY-MM-DD HH:mm',
  'YYYY-MM-DDTHH:mm:ss',
  'YYYY-MM-DDTHH:mm',
  'YYYY-MM-DD HH:mm:ss'
]

export const detectLikelyDatetimeFormat = (datetimeStrings: string[]): AllowedDatetimeFormats => {
  const formatScores = new Map<AllowedDatetimeFormats, number>()
  DATETIME_FORMAT_CANDIDATES.forEach((fmt) => formatScores.set(fmt, 0))

  for (const raw of datetimeStrings) {
    const value = String(raw ?? '').trim()
    if (!value) continue

    for (const fmt of DATETIME_FORMAT_CANDIDATES) {
      if (parseTemporalByFormat(value, fmt, { kind: 'datetime' })) {
        formatScores.set(fmt, (formatScores.get(fmt) || 0) + 1)

        break
      }
    }
    // If none match, the string is skipped.
  }

  let bestFormat: AllowedDatetimeFormats = DATETIME_FORMAT_CANDIDATES[0]
  let bestScore = -1
  for (const [fmt, score] of formatScores.entries()) {
    if (score > bestScore) {
      bestFormat = fmt
      bestScore = score
    }
  }
  return bestFormat
}

export const detectDatetimeFormats = (columns: ITableColumn[], data: Record<string, string>[]) => {
  let detectedDateFormat = null
  let detectedDatetimeFormat = null

  if (data.length > 0) {
    const allDateValues: string[] = []
    const allDatetimeValues: string[] = []
    columns.forEach((column) => {
      if (column.kind === 'date') {
        data.forEach((row) => {
          const val = row[column.publicId]
          if (val && val.trim() !== '') allDateValues.push(val)
        })
      } else if (column.kind === 'datetime') {
        data.forEach((row) => {
          const val = row[column.publicId]
          if (val && val.trim() !== '') allDatetimeValues.push(val)
        })
      }
    })
    if (allDateValues.length > 0) detectedDateFormat = detectLikelyDateFormat(allDateValues)
    if (allDatetimeValues.length > 0) detectedDatetimeFormat = detectLikelyDatetimeFormat(allDatetimeValues)
  }

  return { detectedDateFormat, detectedDatetimeFormat }
}
